import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";

export default function Leaderboards() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [period, setPeriod] = useState("all");
  const [scope, setScope] = useState("global");
  const [rows, setRows] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/leaderboards", { params: { period, scope } });
        setRows(data);
      } catch (_) { setRows([]); }
    })();
  }, [period, scope]);

  return (
    <AppLayout>
      <h1 className="font-display font-extrabold text-4xl tracking-tight mb-6">{t("lb.title")}</h1>

      <div className="flex flex-wrap gap-4 mb-6">
        <Tabs value={period} onValueChange={setPeriod}>
          <TabsList data-testid="lb-period-tabs">
            <TabsTrigger value="all" data-testid="lb-period-all">{t("lb.period.all")}</TabsTrigger>
            <TabsTrigger value="week" data-testid="lb-period-week">{t("lb.period.week")}</TabsTrigger>
            <TabsTrigger value="month" data-testid="lb-period-month">{t("lb.period.month")}</TabsTrigger>
            <TabsTrigger value="year" data-testid="lb-period-year">{t("lb.period.year")}</TabsTrigger>
          </TabsList>
        </Tabs>
        <Tabs value={scope} onValueChange={setScope}>
          <TabsList data-testid="lb-scope-tabs">
            <TabsTrigger value="global" data-testid="lb-scope-global">{t("lb.scope.global")}</TabsTrigger>
            <TabsTrigger value="organization" data-testid="lb-scope-org">{t("lb.scope.org")}</TabsTrigger>
            <TabsTrigger value="country" data-testid="lb-scope-country">{t("lb.scope.country")}</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <Card className="border-border">
        <CardContent className="p-0">
          {rows.length === 0 ? (
            <div className="py-16 text-center text-muted-foreground text-sm">—</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="text-left px-6 py-3 w-16">{t("lb.rank")}</th>
                  <th className="text-left px-6 py-3">{t("lb.player")}</th>
                  <th className="text-right px-6 py-3">{t("lb.games")}</th>
                  <th className="text-right px-6 py-3">{t("lb.wins")}</th>
                  <th className="text-right px-6 py-3">{t("lb.best")}</th>
                  <th className="text-right px-6 py-3">{t("lb.total")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.user_id}
                    className={`border-t border-border ${r.user_id === user.user_id ? "bg-primary/5" : ""}`}
                    data-testid={`lb-row-${i}`}>
                    <td className="px-6 py-3 font-display font-bold">{i + 1}</td>
                    <td className="px-6 py-3">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-8 w-8"><AvatarImage src={r.picture} /><AvatarFallback>{(r.name || "?").slice(0,2).toUpperCase()}</AvatarFallback></Avatar>
                        <div>
                          <div className="font-medium">{r.name}</div>
                          {r.organization && <div className="text-xs text-muted-foreground">{r.organization}</div>}
                        </div>
                        {r.user_id === user.user_id && <Badge variant="secondary" className="ml-2">vous</Badge>}
                      </div>
                    </td>
                    <td className="px-6 py-3 text-right tabular-nums">{r.games_played}</td>
                    <td className="px-6 py-3 text-right tabular-nums">{r.wins}</td>
                    <td className="px-6 py-3 text-right tabular-nums">{r.best_score}</td>
                    <td className="px-6 py-3 text-right tabular-nums font-semibold">{r.total_score}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </AppLayout>
  );
}
